import { useCallback, useState } from 'react'
import Cropper, { Area } from 'react-easy-crop'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import useTranslator from '@/hooks/translation'
import { getCroppedImg } from '@/utils/cropers/image'

import { ImageCropDialogProps } from './types'

export function ImageCropDialog({
  imageSrc,
  onConfirm,
  onCancel,
  aspectRatio = 2 / 1,
}: ImageCropDialogProps) {
  const { getTranslation } = useTranslator()

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number
    y: number
    width: number
    height: number
  } | null>(null)

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleCrop = async () => {
    if (!croppedAreaPixels) return

    const blob = await getCroppedImg(imageSrc, croppedAreaPixels)
    onConfirm(blob)
  }

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>
            {getTranslation('common.modal.croppingImage.title')}
          </DialogTitle>

          <DialogDescription>
            {getTranslation('common.modal.croppingImage.description')}
          </DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="relative h-[300px] w-full min-w-[200px]">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            minZoom={0.5}
            restrictPosition={false}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            cropShape={aspectRatio === 1 ? 'round' : 'rect'}
          />
        </div>

        <DialogFooter className="flex w-full gap-2 sm:justify-between">
          <Button variant="secondary" onClick={onCancel}>
            {getTranslation('common.cancel')}
          </Button>

          <Button onClick={handleCrop} variant="fintalk">
            {getTranslation('common.modal.croppingImage.useImage')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
